import '../../styles/restScreens.css';
import { useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';

// BL-402: the tenant's "Sign-in welcome animation". Admin-only writes are enforced by org_settings RLS.
export const SIGN_IN_WELCOME_OPTIONS = [
    { value: 'film_finale', label: 'Brand film finale', help: '"Welcome, {first name}…", then your logo reveal and chrome sheen. Full on the first sign-in of the day, short after that.' },
    { value: 'fade', label: 'Simple fade', help: 'Your logo fades in and out. No Welcome text.' },
    { value: 'off', label: 'Off', help: 'No animation. A plain fade into the app the moment it is ready.' },
];

export default function SignInWelcomeSetting({ theme = 'light', canManage = false }) {
    const dark = theme === 'dark';
    const [orgId, setOrgId] = useState(null);
    const [value, setValue] = useState(null);
    const [saved, setSaved] = useState(null);
    const [status, setStatus] = useState('loading'); // loading | ready | saving | error
    const [message, setMessage] = useState('');

    useEffect(() => {
        let cancelled = false;
        supabaseClient.from('org_settings').select('org_id, signin_sequence').limit(1).maybeSingle()
            .then(({ data, error }) => {
                if (cancelled) return;
                if (error || !data) {
                    setStatus('error');
                    setMessage(error ? 'Could not load the sign-in welcome setting.' : 'Organization settings are not set up yet.');
                    return;
                }
                setOrgId(data.org_id);
                setValue(data.signin_sequence);
                setSaved(data.signin_sequence);
                setStatus('ready');
            });
        return () => { cancelled = true; };
    }, []);

    const save = async () => {
        setStatus('saving');
        setMessage('');
        const { data, error } = await supabaseClient.from('org_settings')
            .update({ signin_sequence: value, updated_at: new Date().toISOString() })
            .eq('org_id', orgId)
            .select('signin_sequence');
        if (error || !data?.length) {
            setStatus('ready');
            setMessage('Not saved. Only an organization admin can change this.');
            return;
        }
        setSaved(data[0].signin_sequence);
        setStatus('ready');
        setMessage('Saved. It plays at the next sign-in.');
    };

    if (status === 'loading') return <p data-m6-layout="entities" data-m6-component="SignInWelcomeSetting" className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>Loading…</p>;
    if (status === 'error') return <p data-m6-layout="entities" data-m6-component="SignInWelcomeSetting" role="alert" className="text-sm text-red-600">{message}</p>;
    return (
        <fieldset data-m6-layout="entities" data-m6-component="SignInWelcomeSetting" className="space-y-2" disabled={!canManage || status === 'saving'}>
            <legend className={`text-sm mb-2 ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                What plays after someone signs in, while Central Command loads underneath. It never adds to the wait.
            </legend>
            {SIGN_IN_WELCOME_OPTIONS.map((option) => (
                <label key={option.value} className={`flex items-start gap-3 py-2.5 px-3 rounded-lg border cursor-pointer ${dark ? 'border-gray-700' : 'border-gray-200'} ${value === option.value ? 'ring-2 ring-[var(--auth-pill-blue)]' : ''}`}>
                    <input type="radio" name="signin_sequence" value={option.value} checked={value === option.value}
                        onChange={() => { setValue(option.value); setMessage(''); }} className="mt-1" />
                    <span>
                        <span className={`block font-semibold ${dark ? 'text-gray-100' : 'text-gray-800'}`}>{option.label}</span>
                        <span className={`block text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>{option.help}</span>
                    </span>
                </label>
            ))}
            {canManage && (
                <div className="flex items-center gap-3 pt-1">
                    <button type="button" onClick={save} disabled={value === saved || status === 'saving'}
                        className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 font-medium disabled:opacity-50">
                        {status === 'saving' ? 'Saving…' : 'Save'}
                    </button>
                    {message && <span role="status" className={`text-sm ${dark ? 'text-gray-300' : 'text-gray-600'}`}>{message}</span>}
                </div>
            )}
            {!canManage && <p className={`text-xs ${dark ? 'text-gray-500' : 'text-gray-400'}`}>Only an organization admin can change this.</p>}
        </fieldset>
    );
}
